import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Plus, Search, Trash2, Share2, GitFork, Clock, FileCode, Key, Copy, Eye, EyeOff, Code2, AlertCircle, Lock } from "lucide-react";
import { diagrams as diagramsApi, templates as templatesApi, apiKeys as apiKeysApi, auth as authApi } from "@/lib/api";
import { toast } from "sonner";
import Header from "@/components/layout/Header";

export default function Dashboard() {
  const navigate = useNavigate();
  const [myDiagrams, setMyDiagrams] = useState([]);
  const [templateList, setTemplateList] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  
  // API Key state
  const [apiKeyInfo, setApiKeyInfo] = useState(null);
  const [newApiKey, setNewApiKey] = useState(null);
  const [apiKeyLoading, setApiKeyLoading] = useState(false);
  const [showApiKey, setShowApiKey] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [diagramsRes, templatesRes, apiKeyRes] = await Promise.all([
        diagramsApi.list(), 
        templatesApi.list(),
        apiKeysApi.get()
      ]);
      setMyDiagrams(diagramsRes.data);
      setTemplateList(templatesRes.data);
      setApiKeyInfo(apiKeyRes.data);
    } catch {
      toast.error("Failed to load data");
    }
    setLoading(false);
  };

  const handleDelete = async (e, id) => {
    e.stopPropagation();
    try {
      await diagramsApi.delete(id);
      setMyDiagrams((prev) => prev.filter((d) => d.id !== id));
      toast.success("Diagram deleted");
    } catch {
      toast.error("Failed to delete");
    }
  };

  const filteredDiagrams = myDiagrams.filter((d) =>
    d.title.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleGenerateApiKey = async () => {
    setApiKeyLoading(true);
    try {
      const res = await apiKeysApi.generate();
      setNewApiKey(res.data.api_key);
      setApiKeyInfo({ has_key: true, key_prefix: res.data.key_prefix, created_at: res.data.created_at });
      toast.success("API key generated! Save it securely.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to generate API key");
    }
    setApiKeyLoading(false);
  };

  const handleRevokeApiKey = async () => {
    if (!window.confirm("Are you sure you want to revoke your API key? This cannot be undone.")) return;
    setApiKeyLoading(true);
    try {
      await apiKeysApi.revoke();
      setApiKeyInfo({ has_key: false });
      setNewApiKey(null);
      toast.success("API key revoked");
    } catch {
      toast.error("Failed to revoke API key");
    }
    setApiKeyLoading(false);
  };

  const handleCopyApiKey = () => {
    if (newApiKey) {
      navigator.clipboard.writeText(newApiKey);
      toast.success("API key copied to clipboard");
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <div className="max-w-6xl mx-auto px-6 md:px-12 py-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-semibold text-slate-900 font-heading tracking-tight">Dashboard</h1>
            <p className="text-slate-500 mt-1 text-sm font-body">Manage your diagrams and templates</p>
          </div>
          <Button onClick={() => navigate("/editor")} data-testid="create-diagram-btn" className="gap-2">
            <Plus className="h-4 w-4" /> New Diagram
          </Button>
        </div>

        <Tabs defaultValue="my-diagrams">
          <TabsList>
            <TabsTrigger value="my-diagrams" data-testid="my-diagrams-tab">My Diagrams</TabsTrigger>
            <TabsTrigger value="templates" data-testid="templates-tab">Templates</TabsTrigger>
            <TabsTrigger value="api" data-testid="api-tab">
              <Key className="h-3.5 w-3.5 mr-1.5" />
              API Access
            </TabsTrigger>
            <TabsTrigger value="account" data-testid="account-tab">
              <Lock className="h-3.5 w-3.5 mr-1.5" />
              Account
            </TabsTrigger>
          </TabsList>

          <TabsContent value="my-diagrams" className="mt-6">
            <div className="relative mb-5">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Search diagrams..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                data-testid="search-diagrams-input"
              />
            </div>

            {loading ? (
              <div className="text-center py-16 text-slate-400 text-sm">Loading diagrams...</div>
            ) : filteredDiagrams.length === 0 ? (
              <div className="text-center py-16">
                <FileCode className="h-12 w-12 text-slate-300 mx-auto mb-3" strokeWidth={1} />
                <p className="text-slate-500 font-body">No diagrams yet</p>
                <Button variant="outline" className="mt-4" onClick={() => navigate("/editor")} data-testid="empty-create-btn">
                  Create your first diagram
                </Button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredDiagrams.map((diagram) => (
                  <div
                    key={diagram.id}
                    className="border border-slate-200 rounded-md p-4 hover:shadow-md transition-all duration-200 cursor-pointer group"
                    onClick={() => navigate(`/editor/${diagram.id}`)}
                    data-testid={`diagram-card-${diagram.id}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="min-w-0">
                        <h3 className="font-medium text-slate-900 text-sm truncate">{diagram.title}</h3>
                        <p className="text-xs text-slate-400 mt-1 flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {new Date(diagram.updated_at).toLocaleDateString()}
                        </p>
                      </div>
                      <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        {diagram.is_public && <Share2 className="h-3.5 w-3.5 text-blue-500 mt-0.5" />}
                        <button
                          onClick={(e) => handleDelete(e, diagram.id)}
                          className="p-1 hover:bg-red-50 rounded"
                          data-testid={`delete-diagram-${diagram.id}`}
                        >
                          <Trash2 className="h-3.5 w-3.5 text-red-400" />
                        </button>
                      </div>
                    </div>
                    <div className="mt-3 p-2.5 bg-slate-50 rounded text-xs font-mono text-slate-500 h-16 overflow-hidden leading-relaxed">
                      {diagram.dsl_code?.substring(0, 140)}
                    </div>
                    <div className="mt-2.5">
                      <span className="text-[10px] px-2 py-0.5 bg-slate-100 rounded text-slate-500 uppercase tracking-wider font-medium">
                        {diagram.diagram_type}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="templates" className="mt-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {templateList.map((template) => (
                <div
                  key={template.id}
                  className="border border-slate-200 rounded-md p-4 hover:shadow-md transition-all duration-200"
                  data-testid={`template-card-${template.id}`}
                >
                  <h3 className="font-medium text-slate-900 text-sm">{template.title}</h3>
                  <p className="text-xs text-slate-500 mt-1 font-body">{template.description}</p>
                  <div className="mt-3 p-2.5 bg-slate-50 rounded text-xs font-mono text-slate-500 h-16 overflow-hidden leading-relaxed">
                    {template.dsl_code?.substring(0, 140)}
                  </div>
                  <div className="mt-3 flex items-center justify-between">
                    <span className="text-[10px] px-2 py-0.5 bg-slate-100 rounded text-slate-500 uppercase tracking-wider font-medium">
                      {template.category}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigate("/editor", { state: { code: template.dsl_code, title: template.title } })}
                      data-testid={`use-template-${template.id}`}
                    >
                      Use Template
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="api" className="mt-6">
            <div className="max-w-2xl space-y-6">
              {/* API Key Section */}
              <div className="border border-slate-200 rounded-lg p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-slate-100 rounded-lg">
                    <Key className="h-5 w-5 text-slate-600" />
                  </div>
                  <div>
                    <h3 className="font-medium text-slate-900">API Key</h3>
                    <p className="text-xs text-slate-500">Use this key to authenticate API requests</p>
                  </div>
                </div>

                {apiKeyInfo?.has_key ? (
                  <div className="space-y-4">
                    {newApiKey ? (
                      <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                        <div className="flex items-start gap-2 mb-2">
                          <AlertCircle className="h-4 w-4 text-amber-600 mt-0.5" />
                          <p className="text-sm text-amber-800 font-medium">Save your API key now!</p>
                        </div>
                        <p className="text-xs text-amber-700 mb-3">This key won't be shown again. Store it securely.</p>
                        <div className="flex gap-2">
                          <Input
                            value={showApiKey ? newApiKey : "•".repeat(40)}
                            readOnly
                            className="font-mono text-xs"
                            data-testid="api-key-display"
                          />
                          <Button variant="outline" size="icon" onClick={() => setShowApiKey(!showApiKey)} data-testid="toggle-api-key-visibility">
                            {showApiKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                          </Button>
                          <Button variant="outline" size="icon" onClick={handleCopyApiKey} data-testid="copy-api-key-btn">
                            <Copy className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                        <div>
                          <p className="text-sm font-mono text-slate-600">{apiKeyInfo.key_prefix}</p>
                          <p className="text-xs text-slate-400 mt-1">Created: {new Date(apiKeyInfo.created_at).toLocaleDateString()}</p>
                        </div>
                      </div>
                    )}
                    <Button variant="destructive" size="sm" onClick={handleRevokeApiKey} disabled={apiKeyLoading} data-testid="revoke-api-key-btn">
                      <Trash2 className="h-3.5 w-3.5 mr-1.5" />
                      Revoke API Key
                    </Button>
                  </div>
                ) : (
                  <div>
                    <p className="text-sm text-slate-500 mb-4">No API key generated yet.</p>
                    <Button onClick={handleGenerateApiKey} disabled={apiKeyLoading} data-testid="generate-api-key-btn">
                      <Key className="h-4 w-4 mr-2" />
                      {apiKeyLoading ? "Generating..." : "Generate API Key"}
                    </Button>
                  </div>
                )}
              </div>

              {/* API Documentation */}
              <div className="border border-slate-200 rounded-lg p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-slate-100 rounded-lg">
                    <Code2 className="h-5 w-5 text-slate-600" />
                  </div>
                  <div>
                    <h3 className="font-medium text-slate-900">API Documentation</h3>
                    <p className="text-xs text-slate-500">Generate diagrams programmatically</p>
                  </div>
                </div>

                <div className="space-y-4">
                  <div>
                    <h4 className="text-sm font-medium text-slate-700 mb-2">Endpoint</h4>
                    <code className="block p-3 bg-slate-900 text-green-400 rounded-lg text-xs font-mono">
                      POST /api/diagrams/generate-from-source
                    </code>
                  </div>

                  <div>
                    <h4 className="text-sm font-medium text-slate-700 mb-2">Headers</h4>
                    <code className="block p-3 bg-slate-900 text-slate-300 rounded-lg text-xs font-mono whitespace-pre">
{`X-API-Key: your_api_key_here
Content-Type: application/json`}
                    </code>
                  </div>

                  <div>
                    <h4 className="text-sm font-medium text-slate-700 mb-2">Request Body</h4>
                    <code className="block p-3 bg-slate-900 text-slate-300 rounded-lg text-xs font-mono whitespace-pre overflow-x-auto">
{`{
  "content": "Your DSL, natural language, or source code",
  "source_type": "auto",  // "auto", "dsl", "natural_language", "source_code"
  "diagram_type": null,   // Optional: "flowchart", "sequence", "class", etc.
  "language": null        // For source_code: "python", "sql", "javascript"
}`}
                    </code>
                  </div>

                  <div>
                    <h4 className="text-sm font-medium text-slate-700 mb-2">Example: Generate from Natural Language</h4>
                    <code className="block p-3 bg-slate-900 text-slate-300 rounded-lg text-xs font-mono whitespace-pre overflow-x-auto">
{`curl -X POST "${process.env.REACT_APP_BACKEND_URL}/api/diagrams/generate-from-source" \\
  -H "X-API-Key: your_api_key" \\
  -H "Content-Type: application/json" \\
  -d '{"content": "Create a microservice architecture with API gateway, user service, and order service"}'`}
                    </code>
                  </div>

                  <div>
                    <h4 className="text-sm font-medium text-slate-700 mb-2">Example: Generate from Source Code</h4>
                    <code className="block p-3 bg-slate-900 text-slate-300 rounded-lg text-xs font-mono whitespace-pre overflow-x-auto">
{`curl -X POST "${process.env.REACT_APP_BACKEND_URL}/api/diagrams/generate-from-source" \\
  -H "X-API-Key: your_api_key" \\
  -H "Content-Type: application/json" \\
  -d '{"content": "class Animal:\\n  def speak(self):\\n    pass\\n\\nclass Dog(Animal):\\n  def speak(self):\\n    return \\"Bark\\"", "source_type": "source_code", "language": "python"}'`}
                    </code>
                  </div>

                  <div>
                    <h4 className="text-sm font-medium text-slate-700 mb-2">Response</h4>
                    <code className="block p-3 bg-slate-900 text-slate-300 rounded-lg text-xs font-mono whitespace-pre overflow-x-auto">
{`{
  "diagram_id": "uuid",
  "dsl_code": "graph TD\\n  ...",
  "diagram_type": "flowchart",
  "source_type": "natural_language",
  "embed_url": "/embed/uuid",
  "view_url": "/view/uuid",
  "iframe_code": "<iframe src=\\".../embed/uuid\\" ...></iframe>",
  "created_at": "2024-01-01T00:00:00Z"
}`}
                    </code>
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="account" className="mt-6">
            <AccountTab />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

function AccountTab() {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (newPassword.length < 6) {
      toast.error("New password must be at least 6 characters");
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }
    setLoading(true);
    try {
      await authApi.changePassword(currentPassword, newPassword);
      toast.success("Password changed successfully");
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to change password");
    }
    setLoading(false);
  };

  return (
    <div className="max-w-lg">
      <div className="border border-slate-200 rounded-lg p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 bg-slate-100 rounded-lg">
            <Lock className="h-5 w-5 text-slate-600" />
          </div>
          <div>
            <h3 className="font-medium text-slate-900">Change Password</h3>
            <p className="text-xs text-slate-500">Update your account password</p>
          </div>
        </div>

        <form onSubmit={handleChangePassword} className="space-y-4">
          <div className="space-y-2">
            <label className="text-sm font-medium text-slate-700">Current Password</label>
            <div className="relative">
              <input
                type={showCurrentPassword ? "text" : "password"}
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                className="flex h-10 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm ring-offset-white file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-slate-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-950 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                placeholder="Enter current password"
                required
                data-testid="current-password-input"
              />
              <button
                type="button"
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
                onClick={() => setShowCurrentPassword(v => !v)}
              >
                {showCurrentPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-slate-700">New Password</label>
            <div className="relative">
              <input
                type={showNewPassword ? "text" : "password"}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="flex h-10 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm ring-offset-white file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-slate-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-950 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                placeholder="Min 6 characters"
                minLength={6}
                required
                data-testid="new-password-input"
              />
              <button
                type="button"
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
                onClick={() => setShowNewPassword(v => !v)}
              >
                {showNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-slate-700">Confirm New Password</label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="flex h-10 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm ring-offset-white file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-slate-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-950 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
              placeholder="Confirm new password"
              minLength={6}
              required
              data-testid="confirm-password-input"
            />
          </div>

          <Button type="submit" disabled={loading} className="w-full" data-testid="change-password-btn">
            {loading ? "Changing..." : "Change Password"}
          </Button>
        </form>
      </div>
    </div>
  );
}
